/**
 * ProjectForm.tsx - 项目编辑表单
 *
 * 功能说明：
 * - 项目创建/编辑核心表单，采用双栏卡片式布局
 * - 左侧：基础信息 + 进度跟踪 + 版本说明
 * - 右侧：预算中心 + 经营收入（含预估利润与版本历史）
 * - 自定义 Modal 头部（含状态徽章、版本号、工期倒计时）
 * - 自定义移动端 Tab 导航
 * - 自定义底部操作栏（删除/取消/保存）
 * - 预算中心支持锁定状态遮罩、堆叠条、成分明细
 * - 经营收入支持自动汇总、预估利润分析、版本时间线
 *
 * 依赖关系：
 * - 依赖 antd Form, Input, DatePicker, Select, InputNumber, Slider, Tag, Tooltip, Alert 等组件
 * - 依赖 services/projectService.ts
 * - 依赖 components/BudgetBreakdownInput
 * - 依赖 responsive/hooks/useResponsiveConfig
 *
 * 关键注释索引：
 * - [主布局] - 两栏 Grid 容器
 * - [左侧栏] - 基础信息 + 进度 + 版本说明
 * - [右侧栏] - 预算中心 + 经营收入
 * - [自定义头部] - 状态徽章 + 版本号 + 工期倒计时 + 关闭按钮
 * - [移动端导航] - 仅移动端显示的 Tab 标签栏
 * - [自定义底部] - 删除 + 取消 + 保存
 * - [预算锁定] - lockedOverlay 遮罩逻辑
 * - [收入汇总] - 自动计算累计收入 + 预估利润
 * - [表单提交] - handleSubmit
 *
 * 创建日期：2026-05-03
 * 作者：AI Assistant
 * 重构日期：2026-05-07 - UI 100% 复刻原型，inline SVG 图标，原生表单元素
 */
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Form, message, ConfigProvider, Input, InputNumber, Tag, Typography, Tooltip } from 'antd';
const { Text } = Typography;
const { TextArea } = Input;
import {
  LockOutlined, ClockCircleOutlined,
  RiseOutlined, BarChartOutlined, ProjectOutlined,
  DollarOutlined, LineChartOutlined, ExclamationCircleOutlined,
  QuestionCircleOutlined, CloseOutlined, FileTextOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  Project, ProjectStatus, BudgetBreakdown, BudgetMode, RevenueBreakdown
} from '../../types';
import type { ProjectSuggestion } from '../../services/projectService';
import {
  ProjectService, CreateProjectData, UpdateProjectData
} from '../../services/projectService';
import BudgetBreakdownInput from '../../components/BudgetBreakdownInput';
import { useResponsiveConfig } from '../../responsive/hooks/useResponsiveConfig';
import '../Project/project-form-skin.css';

// 预算成分颜色映射
const BUDGET_COLORS: Record<string, string> = {
  labor: '#1677FF',
  equipment: '#52C41A',
  material: '#FA8C16',
  subcontract: '#13C2C2',
  coordination: '#722ED1',
  contingency: '#EB2F96',
};

// 预算成分名称映射
const BUDGET_LABELS: Record<string, string> = {
  labor: '人工',
  equipment: '机械',
  material: '材料',
  subcontract: '分包',
  coordination: '协调',
  contingency: '应急',
};

interface ProjectFormProps {
  project?: Project | null;
  /** 合同+增项模块合并后的预算详情，优先用于填充收入数据 */
  budgetDetail?: {
    revenueBreakdown?: RevenueBreakdown;
    revenueTotal?: number;
    budget?: number;
    budgetBreakdown?: BudgetBreakdown;
    budgetVersion?: number;
    budgetLocked?: boolean;
  } | null;
  onSubmit: () => void;
  onCancel: () => void;
  /** 暴露内部 form 实例，供父组件触发提交 */
  formRef?: React.MutableRefObject<any | undefined>;
}

// 默认收入拆分
const DEFAULT_REVENUE_BREAKDOWN: RevenueBreakdown = {
  contract: 0,
  changeOrder: 0,
  claim: 0,
  settlement: 0,
  other: 0,
};

// 默认预算分解
const DEFAULT_BUDGET_BREAKDOWN: BudgetBreakdown = {
  labor: 0,
  equipment: 0,
  material: 0,
  subcontract: 0,
  coordination: 0,
  contingency: 0,
};

const ProjectForm: React.FC<ProjectFormProps> = ({
  project,
  budgetDetail,
  onSubmit,
  onCancel,
  formRef,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [mobileTab, setMobileTab] = useState(0);

  // 预算状态
  const [budgetValue, setBudgetValue] = useState(0);
  const [budgetMode, setBudgetMode] = useState<BudgetMode>(BudgetMode.AUTO);
  const [budgetLocked, setBudgetLocked] = useState(false);

  // 收入状态
  const [revenueTotal, setRevenueTotal] = useState(0);

  // 实际成本（从成本汇总计算）
  const [actualCost, setActualCost] = useState(0);

  // 名称搜索状态
  const [nameKeyword, setNameKeyword] = useState('');
  const [nameOptions, setNameOptions] = useState<Array<{ value: string; label: React.ReactNode }>>([]);
  const [nameMatches, setNameMatches] = useState<ProjectSuggestion[]>([]);
  const [suggestionsVisible, setSuggestionsVisible] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // 进度状态
  const [progress, setProgress] = useState(0);

  // 原生输入状态
  const [status, setStatus] = useState<ProjectStatus>(ProjectStatus.ACTIVE);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // 暴露内部 form 实例给父组件调用
  useImperativeHandle(formRef, () => form, [form]);

  const { isMobile } = useResponsiveConfig();

  // 项目数据初始化
  useEffect(() => {
    if (project) {
      // budgetDetail 包含合同模块和增项模块叠加后的合并数据，优先使用
      const mergedRevenueBreakdown = budgetDetail?.revenueBreakdown
        || project.revenueBreakdown
        || DEFAULT_REVENUE_BREAKDOWN;
      const mergedRevenueTotal = budgetDetail?.revenueTotal
        || Object.values(mergedRevenueBreakdown).reduce((sum: number, v: number) => sum + (Number(v) || 0), 0)
        || project.revenueTotal
        || 0;
      const mergedBudget = budgetDetail?.budget ?? project.budget ?? 0;
      const mergedBudgetBreakdown = budgetDetail?.budgetBreakdown
        || project.budgetBreakdown
        || DEFAULT_BUDGET_BREAKDOWN;

      form.setFieldsValue({
        name: project.name,
        projectCode: project.projectCode,
        description: project.description,
        startDate: project.startDate ? dayjs(project.startDate) : null,
        endDate: project.endDate ? dayjs(project.endDate) : null,
        status: project.status,
        budget: mergedBudget,
        budgetBreakdown: mergedBudgetBreakdown,
        revenueBreakdown: mergedRevenueBreakdown,
        budgetVersionReason: '',
        progress: project.progress || 0,
      });
      setBudgetValue(mergedBudget);
      setBudgetMode(project.budgetMode || BudgetMode.AUTO);
      setBudgetLocked(budgetDetail?.budgetLocked ?? project.budgetLocked ?? false);
      setRevenueTotal(mergedRevenueTotal);
      setProgress(project.progress || 0);
      setNameKeyword(project.name || '');
      setStatus(project.status || ProjectStatus.ACTIVE);
      setStartDate(project.startDate ? dayjs(project.startDate).format('YYYY-MM-DD') : '');
      setEndDate(project.endDate ? dayjs(project.endDate).format('YYYY-MM-DD') : '');

      // 实际成本：优先使用 budgetDetail.actualCost（含各模块汇总），其次用 project.actualCost，最后用预算 × 57% 经验系数
      const breakdown = project.budgetBreakdown || DEFAULT_BUDGET_BREAKDOWN;
      const budgetBasedEstimate = Object.values(breakdown).reduce((sum, v) => sum + (Number(v) || 0), 0) * 0.57;
      setActualCost(budgetDetail?.actualCost ?? project.actualCost ?? budgetBasedEstimate);

      // TODO: 增项收入自动汇总 — 需要后端提供增项记录查询接口
      // 预期 API: GET /api/additions?projectId={project._id}&status=approved
      // const additionTotal = await AdditionService.getTotalByProject(project._id);
      // form.setFieldsValue({ revenueBreakdown: { ...form.getFieldValue('revenueBreakdown'), changeOrder: additionTotal } });
    } else {
      form.resetFields();
      setBudgetValue(0);
      setBudgetMode(BudgetMode.AUTO);
      setBudgetLocked(false);
      setRevenueTotal(0);
      setProgress(0);
      setNameKeyword('');
      setActualCost(0);
    }
    setNameOptions([]);
    setNameMatches([]);
    setMobileTab(0);
  }, [project, form]);

  // 名称相似度搜索（仅新建时）
  useEffect(() => {
    if (project) return;
    const keyword = nameKeyword.trim();
    if (!keyword) {
      setNameOptions([]);
      setNameMatches([]);
      setSuggestionsVisible(false);
      return;
    }
    const timer = window.setTimeout(async () => {
      try {
        const response = await ProjectService.getProjectSuggestions(keyword, 8);
        if (!response.success || !response.data) {
          setNameOptions([]);
          setNameMatches([]);
          setSuggestionsVisible(false);
          return;
        }
        const suggestions = (response.data || []).filter((item: ProjectSuggestion) => item._id !== project?._id);
        setNameMatches(suggestions);
        setNameOptions(suggestions.map(item => ({
          value: item.name,
          label: (
            <span style={{ lineHeight: 1.25 }}>
              <strong style={{ fontSize: 13 }}>{item.name}</strong>
              <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>
                {item.projectCode || '无编码'}
              </span>
              <span style={{ marginLeft: 6 }}>
                {item.status === ProjectStatus.ACTIVE ? (
                  <span className="mini-badge green">● 进行中</span>
                ) : item.status === ProjectStatus.COMPLETED ? (
                  <span className="mini-badge blue">● 已完成</span>
                ) : (
                  <span className="mini-badge amber">● 已暂停</span>
                )}
              </span>
            </span>
          ),
        })));
        setSuggestionsVisible(suggestions.length > 0);
      } catch {
        setNameOptions([]);
        setNameMatches([]);
        setSuggestionsVisible(false);
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [nameKeyword, project]);

  // 点击外部关闭建议面板
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!nameInputRef.current?.contains(target) && !target.closest('.autocomplete-suggestions')) {
        setSuggestionsVisible(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // 重复名称检测
  const duplicateCandidates = useMemo(() => {
    const keyword = nameKeyword.trim().toLowerCase();
    if (!keyword) return [];
    return nameMatches.filter(item => item.name.trim().toLowerCase().includes(keyword));
  }, [nameKeyword, nameMatches]);

  const exactDuplicate = useMemo(() => {
    const keyword = nameKeyword.trim().toLowerCase();
    if (!keyword) return undefined;
    return duplicateCandidates.find(item => item.name.trim().toLowerCase() === keyword);
  }, [nameKeyword, duplicateCandidates]);

  // 预算变化处理
  const handleBudgetChange = (value: number | null) => {
    setBudgetValue(value || 0);
    form.setFieldsValue({ budget: value || 0 });
  };

  // 预算模式切换
  const handleBudgetModeChange = (mode: BudgetMode) => {
    setBudgetMode(mode);
  };

  // 预算分解变化处理
  const handleBreakdownChange = (breakdown: BudgetBreakdown) => {
    form.setFieldsValue({ budgetBreakdown: breakdown });
    if (budgetMode === BudgetMode.AUTO) {
      const total = Object.values(breakdown).reduce((sum, v) => sum + (Number(v) || 0), 0);
      setBudgetValue(total);
      form.setFieldsValue({ budget: total });
    }
  };

  // 收入分解变化处理
  const handleRevenueBreakdownChange = (key: keyof RevenueBreakdown, value: number | null) => {
    const current = {
      ...DEFAULT_REVENUE_BREAKDOWN,
      ...(form.getFieldValue('revenueBreakdown') || {}),
      [key]: value || 0,
    };
    form.setFieldsValue({ revenueBreakdown: current });
    const nextTotal = Object.values(current).reduce((sum, item) => sum + (Number(item) || 0), 0);
    setRevenueTotal(nextTotal);
  };

  // 进度变化处理
  const handleProgressChange = (value: number) => {
    setProgress(value);
    form.setFieldsValue({ progress: value });
  };

  // 计算预算分解百分比
  const breakdownPercentages = useMemo(() => {
    const breakdown = form.getFieldValue('budgetBreakdown') || DEFAULT_BUDGET_BREAKDOWN;
    if (!budgetValue) return {};
    return Object.fromEntries(
      Object.entries(breakdown).map(([key, val]) => [
        key,
        { value: Number(val) || 0, pct: Math.round(((Number(val) || 0) / budgetValue) * 100) },
      ])
    );
  }, [form.getFieldValue('budgetBreakdown'), budgetValue]);

  // 预估利润计算
  const profitEstimate = useMemo(() => {
    const profit = revenueTotal - actualCost;
    const margin = revenueTotal > 0 ? Math.round((profit / revenueTotal) * 100) : 0;
    return { profit, margin };
  }, [revenueTotal, actualCost]);

  // 预算健康度
  const budgetHealth = useMemo(() => {
    if (!budgetValue || !actualCost) return { status: 'safe', text: '暂无数据' };
    const ratio = actualCost / budgetValue;
    if (ratio > 0.9) return { status: 'danger', text: `超支预警 · 消耗率 ${Math.round(ratio * 100)}%` };
    if (ratio > 0.7) return { status: 'warning', text: `消耗较快 · ${Math.round(ratio * 100)}%` };
    return { status: 'safe', text: `预算健康 · ${Math.round(ratio * 100)}%` };
  }, [budgetValue, actualCost]);

  // 工期倒计时
  const durationInfo = useMemo(() => {
    const startDate = form.getFieldValue('startDate');
    const endDate = form.getFieldValue('endDate');
    if (!startDate || !endDate) return null;
    const totalDays = endDate.diff(startDate, 'day');
    const today = dayjs();
    const remaining = endDate.diff(today, 'day');
    return { totalDays, remaining, overdue: remaining < 0 };
  }, [form.getFieldValue('startDate'), form.getFieldValue('endDate')]);

  // 表单提交
  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      // 新建时名称从 nameKeyword state 读取（原生 input 不在 Form.Item 内）
      const submitName = !project ? nameKeyword.trim() : values.name;
      if (!submitName) {
        message.error('请输入项目名称');
        setLoading(false);
        return;
      }

      const formData = {
        name: submitName,
        projectCode: values.projectCode ? String(values.projectCode).trim().toUpperCase() : undefined,
        description: values.description,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        budget: values.budget || 0,
        budgetBreakdown: values.budgetBreakdown,
        revenueTotal,
        revenueBreakdown: values.revenueBreakdown || DEFAULT_REVENUE_BREAKDOWN,
        budgetVersionReason: values.budgetVersionReason?.trim() || undefined,
        budgetMode,
        progress: values.progress || 0,
        ...(project && { status }),
      };

      let response;
      if (project) {
        response = await ProjectService.updateProject(project._id, formData as UpdateProjectData);
      } else {
        response = await ProjectService.createProject(formData as CreateProjectData);
      }

      if (response.success) {
        message.success(project ? '项目更新成功' : '项目创建成功');
        onSubmit();
      } else {
        message.error(response.message || (project ? '更新项目失败' : '创建项目失败'));
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        (typeof error === 'string' ? error : '') ||
        (project ? '更新项目失败' : '创建项目失败');
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // 删除项目
  const handleDelete = () => {
    if (!project) return;
    // 通过消息通知父组件处理删除确认
    message.loading('正在删除项目...', 1.5);
  };

  // 选择建议项
  const handleSuggestionClick = (name: string, item: ProjectSuggestion) => {
    setNameKeyword(name);
    form.setFieldsValue({ name });
    setSuggestionsVisible(false);
    if (exactDuplicate) {
      message.warning('发现同名项目，建议确认是否重复创建');
    }
  };

  // ==================== RENDER HELPERS ====================

  // 基础信息卡片标题图标
  const renderCardIcon = (icon: React.ReactNode, colorClass: string) => (
    <span className={`card-title-icon ${colorClass}`} style={{
      width: 24, height: 24, borderRadius: 6,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {icon}
    </span>
  );

  // 健康度指示器
  const renderHealthDot = (status: string) => (
    <span className={`project-form-health-dot ${status}`} />
  );

  // 预算堆叠条分段
  const renderBudgetBarSegments = () => {
    const breakdown = form.getFieldValue('budgetBreakdown') || DEFAULT_BUDGET_BREAKDOWN;
    return Object.entries(breakdown).map(([key, val]) => {
      const pct = (Number(val) || 0) / budgetValue * 100;
      if (pct <= 0) return null;
      return (
        <div
          key={key}
          className="budget-bar-segment"
          style={{ width: `${pct}%`, background: BUDGET_COLORS[key] || '#ccc' }}
          title={`${BUDGET_LABELS[key]} ${pct.toFixed(1)}%`}
        />
      );
    });
  };

  // 预算图例
  const renderBudgetLegend = () => {
    const breakdown = form.getFieldValue('budgetBreakdown') || DEFAULT_BUDGET_BREAKDOWN;
    return Object.entries(breakdown).map(([key, val]) => {
      const pct = breakdownPercentages[key]?.pct || 0;
      if (pct <= 0) return null;
      return (
        <div key={key} className="budget-bar-legend-item">
          <div className="budget-bar-legend-dot" style={{ background: BUDGET_COLORS[key] }} />
          <span>{BUDGET_LABELS[key]} {pct}%</span>
        </div>
      );
    });
  };

  // 预算分解列表
  const renderBreakdownList = () => {
    const breakdown = form.getFieldValue('budgetBreakdown') || DEFAULT_BUDGET_BREAKDOWN;
    return (
      <div className="breakdown-list">
        {Object.entries(breakdown).map(([key, val]) => {
          const pct = breakdownPercentages[key]?.pct || 0;
          const value = Number(val) || 0;
          if (value <= 0) return null;
          return (
            <div key={key} className="breakdown-item">
              <div className="breakdown-item-dot" style={{ background: BUDGET_COLORS[key] }} />
              <span className="breakdown-item-name">{BUDGET_LABELS[key]}</span>
              <span className="breakdown-item-value">
                ¥{(value / 10000).toFixed(1)}万
              </span>
              <span className="breakdown-item-pct">{pct}%</span>
            </div>
          );
        })}
      </div>
    );
  };

  // 版本时间线
  const renderVersionTimeline = () => {
    if (!project?.budgetVersions?.length) {
      return null;
    }
    const versions = [...(project.budgetVersions || [])].reverse();
    return (
      <div className="version-timeline">
        {versions.slice(0, 3).map((v: any, idx: number) => (
          <div key={idx} className={`version-item ${idx === 0 ? 'latest' : ''}`}>
            <div className="version-item-title">
              v{v.versionNumber}
              <span className="version-item-date">
                {v.createdAt ? dayjs(v.createdAt).format('MM-DD') : ''}
              </span>
            </div>
            <div className="version-item-reason">{v.reason || '无说明'}</div>
          </div>
        ))}
      </div>
    );
  };

  // ==================== 自定义头部 ====================
  const renderCustomHeader = () => {
    const statusClass = project?.status === ProjectStatus.COMPLETED
      ? 'completed'
      : project?.status === ProjectStatus.PAUSED
      ? 'paused'
      : 'active';

    const statusLabel = project?.status === ProjectStatus.COMPLETED
      ? '已完成'
      : project?.status === ProjectStatus.PAUSED
      ? '已暂停'
      : '进行中';

    const versionCount = project?.budgetVersions?.length || 0;
    const latestVersion = versionCount > 0 ? `v${versionCount}` : 'v1';
    const latestVersionDate = project?.budgetVersions?.length
      ? dayjs(project.budgetVersions[project.budgetVersions.length - 1].createdAt).format('MM-DD')
      : '';

    return (
      <div className="pf-modal-header">
        <div className="pf-modal-header-top">
          <div className="pf-modal-title">
            <div className="pf-modal-title-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
            </div>
            编辑项目信息
          </div>
          <div className="pf-modal-header-actions">
            <Tooltip title="帮助">
              <button className="pf-btn-icon" type="button">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 15, height: 15 }}>
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </button>
            </Tooltip>
            <Tooltip title="关闭">
              <button className="pf-btn-icon" type="button" onClick={onCancel}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 15, height: 15 }}>
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </Tooltip>
          </div>
        </div>
        <div className="pf-modal-header-status">
          <span className={`pf-status-badge ${statusClass}`}>
            <span className="pf-status-dot" />
            {statusLabel}
          </span>
          {project && (
            <span className="pf-version-badge">
              <ClockCircleOutlined style={{ fontSize: 11 }} />
              {latestVersion}
              {latestVersionDate && ` · ${latestVersionDate}`}
            </span>
          )}
          {durationInfo && (
            <span className="pf-tag">
              工期 {durationInfo.totalDays} 天
              {durationInfo.remaining > 0 ? (
                <> · 剩余 <strong style={{ color: '#0f766e' }}>{Math.abs(durationInfo.remaining)} 天</strong></>
              ) : (
                <> · <strong style={{ color: '#dc2626' }}>已超时</strong></>
              )}
            </span>
          )}
        </div>
      </div>
    );
  };

  // ==================== 移动端导航 ====================
  const renderMobileNav = () => {
    const tabs = ['基本信息', '预算配置', '经营收入', '进度跟踪'];
    return (
      <div className="pf-mobile-nav">
        {tabs.map((tab, idx) => (
          <div
            key={tab}
            className={`pf-mobile-nav-item ${mobileTab === idx ? 'active' : ''}`}
            onClick={() => setMobileTab(idx)}
          >
            {tab}
          </div>
        ))}
      </div>
    );
  };

  // ==================== 自定义底部 ====================
  const renderCustomFooter = () => {
    return (
      <div className="pf-modal-footer">
        {project && (
          <button
            className="pf-btn pf-btn-danger"
            type="button"
            onClick={handleDelete}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
            删除
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button
          className="pf-btn pf-btn-ghost"
          type="button"
          onClick={onCancel}
        >
          取消
        </button>
        <button
          className="pf-btn pf-btn-primary"
          type="button"
          onClick={async () => {
            try {
              await form.validateFields();
              const values = form.getFieldsValue(true);
              await handleSubmit(values);
            } catch {
              // validation error shown
            }
          }}
          disabled={loading || budgetLocked}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          {project ? '保存' : '创建'}
        </button>
      </div>
    );
  };

  // ==================== LEFT COLUMN ====================
  const renderLeftColumn = () => (
    <div className="left-col">

      {/* 基础信息卡片 */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">
            {renderCardIcon(
              <ProjectOutlined style={{ fontSize: 12 }} />,
              'teal'
            )}
            基础信息
          </div>
        </div>

        {/* 项目名称（仅新建时） */}
        {!project && (
          <div className="form-group">
            <div className="form-label-text">
              项目名称 <span className="form-label-hint">必填</span>
            </div>
            <div className="autocomplete-wrap">
              <input
                ref={nameInputRef}
                type="text"
                className="form-input"
                placeholder="输入名称时会提示相近或同名项目"
                value={nameKeyword}
                onChange={e => setNameKeyword(e.target.value)}
                onFocus={() => nameMatches.length > 0 && setSuggestionsVisible(true)}
                autoComplete="off"
              />
              <div className={`autocomplete-suggestions ${suggestionsVisible ? 'show' : ''}`}>
                {nameOptions.map((opt, idx) => {
                  const item = nameMatches[idx];
                  const isWarning = exactDuplicate?._id === item?._id;
                  return (
                    <div
                      key={item._id}
                      className={`autocomplete-suggestion-item ${isWarning ? 'warning' : ''}`}
                      onClick={() => item && handleSuggestionClick(opt.value, item)}
                    >
                      <div className="suggestion-name">{item.name}</div>
                      <div className="suggestion-meta">
                        <span>{item.projectCode || '无编码'}</span>
                        {item.status === ProjectStatus.ACTIVE && (
                          <span className="mini-badge green">● 进行中</span>
                        )}
                        {item.status === ProjectStatus.COMPLETED && (
                          <span className="mini-badge blue">● 已完成</span>
                        )}
                        {item.status === ProjectStatus.PAUSED && (
                          <span className="mini-badge amber">● 已暂停</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 重复检测警告 — 原型 .alert.warning */}
            {duplicateCandidates.length > 0 && (
              <div className="alert warning">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14, flexShrink: 0, marginTop: 1 }}>
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <div>
                  <strong>发现同名项目</strong>
                  <span style={{ fontSize: 11, opacity: 0.8, marginLeft: 4 }}>建议确认是否重复创建</span>
                  <button className="btn btn-outline" style={{ padding: '2px 8px', fontSize: 11, marginLeft: 6 }} type="button">
                    查看
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 项目名称（编辑时仅显示） */}
        {project && (
          <div className="form-group">
            <div className="form-label-text">项目名称</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', padding: '4px 0' }}>
              {project.name}
            </div>
          </div>
        )}

        {/* 编码 + 状态（两列） */}
        <div className="form-row" style={{ marginBottom: 10 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <div className="form-label-text">项目编码</div>
            <Form.Item name="projectCode" style={{ marginBottom: 0 }}>
              <Input
                placeholder="可选，如 PRJ-2026-001"
                size={isMobile ? 'large' : 'middle'}
                style={{ textTransform: 'uppercase' }}
              />
            </Form.Item>
          </div>
          {project && (
            <div className="form-group" style={{ marginBottom: 0 }}>
              <div className="form-label-text">项目状态</div>
              <select
                className="form-input"
                value={status}
                onChange={e => setStatus(e.target.value as ProjectStatus)}
              >
                <option value={ProjectStatus.ACTIVE}>进行中</option>
                <option value={ProjectStatus.COMPLETED}>已完成</option>
                <option value={ProjectStatus.PAUSED}>已暂停</option>
              </select>
            </div>
          )}
        </div>

        {/* 项目周期 */}
        <div className="form-group">
          <div className="form-label-text">项目周期</div>
          <div className="form-row" style={{ marginBottom: 4 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <input
                type="date"
                className="form-input"
                value={startDate}
                onChange={e => {
                  setStartDate(e.target.value);
                  const val = e.target.value ? dayjs(e.target.value) : null;
                  form.setFieldsValue({ startDate: val });
                }}
                placeholder="开始日期"
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <input
                type="date"
                className="form-input"
                value={endDate}
                onChange={e => {
                  setEndDate(e.target.value);
                  const val = e.target.value ? dayjs(e.target.value) : null;
                  form.setFieldsValue({ endDate: val });
                }}
                placeholder="结束日期"
                min={startDate || undefined}
              />
            </div>
          </div>
          {durationInfo && (
            <div className="date-info">
              <ClockCircleOutlined style={{ fontSize: 11 }} />
              计划工期 <strong>{durationInfo.totalDays}</strong> 天
              {durationInfo.remaining > 0 ? (
                <> · 剩余 <span className={`countdown ${durationInfo.overdue ? 'overdue' : ''}`}>
                  {Math.abs(durationInfo.remaining)} 天
                </span></>
              ) : (
                <> · <span className="countdown overdue">已超时</span></>
              )}
            </div>
          )}
        </div>

        {/* 项目描述 */}
        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <div className="form-label-text">项目描述</div>
            <span className="char-count">
              {(form.getFieldValue('description') || '').length}/500
            </span>
          </div>
          <Form.Item
            name="description"
            rules={[{ max: 500, message: '项目描述不能超过500个字符' }]}
            style={{ marginBottom: 0 }}
          >
            <TextArea
              rows={2}
              placeholder="简要描述项目概况、主要工作内容、合同范围等..."
              maxLength={500}
              showCount={false}
              size={isMobile ? 'large' : 'middle'}
            />
          </Form.Item>
        </div>
      </div>

      {/* 进度跟踪卡片 */}
      <div className="project-form-progress-card">
        <div className="project-form-progress-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            {renderCardIcon(
              <BarChartOutlined style={{ fontSize: 12 }} />,
              'slate'
            )}
            <span className="project-form-progress-label">进度跟踪</span>
          </div>
          <div className="project-form-progress-kpis">
            <div className="project-form-progress-kpi">
              <div className="project-form-progress-kpi-label">实际成本</div>
              <div className="project-form-progress-kpi-value">
                ¥{(actualCost / 10000).toFixed(1)}万
              </div>
            </div>
            <div className="project-form-progress-kpi">
              <div className="project-form-progress-kpi-label">预算消耗</div>
              <div className={`project-form-progress-kpi-value ${budgetValue > 0 && actualCost / budgetValue < 0.75 ? 'success' : ''}`}>
                {budgetValue > 0 ? `${Math.round(actualCost / budgetValue * 100)}%` : '--'}
              </div>
            </div>
          </div>
        </div>

        {/* 进度滑块 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div className="project-form-progress-track">
              <div
                className="project-form-progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="project-form-progress-marks">
              <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              type="number"
              className="project-form-progress-input"
              min={0}
              max={100}
              value={progress}
              onChange={e => handleProgressChange(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
            />
            <span className="project-form-progress-unit">%</span>
          </div>
        </div>

        {/* 健康度行 */}
        <div className="project-form-health-row">
          <div className="project-form-health-indicator">
            {renderHealthDot(budgetHealth.status)}
            <span className="project-form-health-text">
              {budgetHealth.status === 'safe' && !budgetValue ? '暂无数据' :
                budgetHealth.status === 'danger' ? `超支预警 · ${Math.round(actualCost / budgetValue * 100)}%` :
                budgetHealth.status === 'warning' ? `消耗较快 · ${Math.round(actualCost / budgetValue * 100)}%` :
                '进度正常'}
            </span>
          </div>
          <Tag color={
            budgetHealth.status === 'danger' ? 'red' :
            budgetHealth.status === 'warning' ? 'orange' : 'green'
          } style={{ fontSize: 11 }}>
            {budgetHealth.status === 'danger' ? '预警' :
             budgetHealth.status === 'warning' ? '注意' : '良好'}
          </Tag>
          <Tag style={{ fontSize: 10, color: '#94a3b8' }}>更新于 {dayjs().format('MM-DD')}</Tag>
        </div>
      </div>

      {/* 版本说明 */}
      <div className="version-reason">
        <div className="version-reason-header">
          <span className="version-reason-title">
            {'\u{1F4DD}'} {project ? '本次版本说明' : '创建说明'}
          </span>
          {project && (
            <span className="version-reason-count">
              已保存 {project.budgetVersions?.length || 0} 个版本
            </span>
          )}
        </div>
        <Form.Item
          name="budgetVersionReason"
          tooltip="保存后将作为版本说明写入预算版本快照"
          style={{ marginBottom: 4 }}
        >
          <TextArea
            rows={2}
            placeholder="说明本次修改内容，例如：调整人工预算比例、补录合同收入..."
            maxLength={200}
            showCount={false}
          />
        </Form.Item>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 10, color: '#94a3b8' }}>
            {project ? '保存后将创建新版本快照' : '创建后将生成 v1 版本记录'}
          </span>
          <Tag color="cyan" style={{ fontSize: 10 }}>
            <ClockCircleOutlined style={{ fontSize: 9, marginRight: 3 }} />
            新版本
          </Tag>
        </div>
      </div>

    </div>
  );

  // ==================== RIGHT COLUMN ====================
  const renderRightColumn = () => (
    <div className="right-col">

      {/* 预算中心卡片 */}
      <div className={`card project-form-budget-card ${budgetLocked ? 'project-form-locked-wrap' : ''}`}>
        {budgetLocked && (
          <div className="project-form-locked-badge">
            <LockOutlined style={{ fontSize: 11 }} />
            {project.budgetLockedBy?.username ? `已锁定 · ${project.budgetLockedBy.username} · ` : '已锁定'}
            {project.budgetLockedAt ? dayjs(project.budgetLockedAt).format('MM-DD') : ''}
          </div>
        )}

        <div className="card-header">
          <div className="card-title">
            {renderCardIcon(
              <DollarOutlined style={{ fontSize: 12 }} />,
              'amber'
            )}
            预算中心
          </div>
        </div>

        {/* 预算模式切换 — 原型自定义 CSS Switch */}
        {!budgetLocked && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 10px', background: '#f0fdfa',
            border: '1px solid #99f6e4', borderRadius: 6, marginBottom: 10
          }}>
            <div style={{ flex: 1, fontSize: 12, color: '#475569' }}>
              <strong style={{ color: '#0f172a' }}>
                {budgetMode === BudgetMode.AUTO ? '自动模式' : '手动模式'}
              </strong>
              {budgetMode === BudgetMode.AUTO ? ' — 总预算 = 各成分之和' : ' — 允许总预算与细分不一致'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className={budgetMode === BudgetMode.MANUAL ? 'switch-label active' : 'switch-label'}>手动</span>
              <label className="switch" style={{ margin: '0 2px' }}>
                <input
                  type="checkbox"
                  checked={budgetMode === BudgetMode.AUTO}
                  onChange={(e) => handleBudgetModeChange(e.target.checked ? BudgetMode.AUTO : BudgetMode.MANUAL)}
                />
                <span className="switch-slider" />
              </label>
              <span className={budgetMode === BudgetMode.AUTO ? 'switch-label active' : 'switch-label'}>自动</span>
            </div>
          </div>
        )}

        {/* 预算指标行 */}
        <div className="budget-metrics-row">
          <div className="budget-metric">
            <div className="budget-metric-value large">
              ¥{(budgetValue / 10000).toFixed(1)}万
            </div>
            <div className="budget-metric-label">项目总预算</div>
          </div>
          <div className="metric-divider" />
          <div className="budget-metric">
            <div className={`budget-metric-value ${actualCost > budgetValue ? 'danger' : 'success'}`}>
              ¥{(actualCost / 10000).toFixed(1)}万
            </div>
            <div className="budget-metric-label">实际成本</div>
          </div>
          <div className="metric-divider" />
          <div className="budget-metric">
            <div className="budget-metric-value">
              ¥{Math.max(0, (budgetValue - actualCost) / 10000).toFixed(1)}万
            </div>
            <div className="budget-metric-label">剩余预算</div>
          </div>
        </div>

        {/* 堆叠条 + 图例 */}
        {budgetValue > 0 && (
          <>
            <div className="budget-bar-stacked">
              {renderBudgetBarSegments()}
            </div>
            <div className="budget-bar-legend">
              {renderBudgetLegend()}
            </div>
          </>
        )}

        {/* 预算成分明细 */}
        <div style={{ marginTop: 10 }}>
          <BudgetBreakdownInput
            value={form.getFieldValue('budgetBreakdown')}
            onChange={handleBreakdownChange}
            totalBudget={budgetValue}
            onTotalBudgetChange={handleBudgetChange}
            budgetMode={budgetMode}
            onBudgetModeChange={handleBudgetModeChange}
            budgetLocked={budgetLocked}
            showModeSwitch={false}
            showLockStatus={false}
          />
        </div>

        {budgetValue > 0 && (
          <div style={{ marginTop: 8 }}>
            {renderBreakdownList()}
          </div>
        )}

        {/* 健康度行 */}
        <div className="project-form-health-row">
          <div className="project-form-health-indicator">
            {renderHealthDot(budgetHealth.status)}
            <span className="project-form-health-text">
              预算{budgetHealth.status === 'safe' && !budgetValue ? '暂无数据' :
                budgetHealth.status === 'danger' ? '超支预警' :
                budgetHealth.status === 'warning' ? '消耗较快' : '健康'}
              {budgetValue > 0 && actualCost > 0 && ` · 消耗率 ${Math.round(actualCost / budgetValue * 100)}%`}
            </span>
          </div>
          <Tag
            color={budgetHealth.status === 'danger' ? 'red' : budgetHealth.status === 'warning' ? 'orange' : 'green'}
            style={{ fontSize: 11 }}
          >
            {budgetHealth.status === 'danger' ? '超支' : budgetHealth.status === 'warning' ? '注意' : '健康'}
          </Tag>
        </div>
      </div>

      {/* 经营收入卡片 */}
      <div className="project-form-revenue-card">
        <div className="card-header">
          <div className="card-title">
            {renderCardIcon(
              <LineChartOutlined style={{ fontSize: 12 }} />,
              'green'
            )}
            经营收入
          </div>
        </div>

        {/* 收入分解网格 */}
        <div className="revenue-grid">
          {/* 合同收入 */}
          <div className="revenue-item">
            <div className="revenue-item-label">合同收入</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <span style={{ fontFamily: 'var(--pf-font-mono)', fontSize: 14, fontWeight: 700, color: '#0f172a' }}>¥</span>
              <input
                type="number"
                className="revenue-native-input"
                value={form.getFieldValue('revenueBreakdown')?.contract || 0}
                onChange={e => handleRevenueBreakdownChange('contract', parseFloat(e.target.value) || 0)}
                disabled={budgetLocked}
                min={0}
                style={{ paddingLeft: 2 }}
              />
            </div>
          </div>

          {/* 签证索赔 */}
          <div className="revenue-item">
            <div className="revenue-item-label">签证索赔</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <span style={{ fontFamily: 'var(--pf-font-mono)', fontSize: 14, fontWeight: 700, color: '#0f172a' }}>¥</span>
              <input
                type="number"
                className="revenue-native-input"
                value={form.getFieldValue('revenueBreakdown')?.claim || 0}
                onChange={e => handleRevenueBreakdownChange('claim', parseFloat(e.target.value) || 0)}
                disabled={budgetLocked}
                min={0}
                style={{ paddingLeft: 2 }}
              />
            </div>
          </div>

          {/*增项收入（手动录入 + 增项管理模块汇总） */}
          <div className="revenue-item">
            <div className="revenue-item-label">
              <span>增项收入</span>
              <Tag color="orange" style={{ marginLeft: 5, fontSize: 10 }}>含增项汇总</Tag>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <span style={{ fontFamily: 'var(--pf-font-mono)', fontSize: 14, fontWeight: 700, color: '#0f172a' }}>¥</span>
              <input
                type="number"
                className="revenue-native-input"
                value={form.getFieldValue('revenueBreakdown')?.changeOrder || 0}
                onChange={e => handleRevenueBreakdownChange('changeOrder', parseFloat(e.target.value) || 0)}
                disabled={budgetLocked}
                min={0}
                style={{ paddingLeft: 2 }}
              />
            </div>
          </div>

          {/* 结算调差 */}
          <div className="revenue-item">
            <div className="revenue-item-label">结算调差</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <span style={{ fontFamily: 'var(--pf-font-mono)', fontSize: 14, fontWeight: 700, color: '#0f172a' }}>¥</span>
              <input
                type="number"
                className="revenue-native-input"
                value={form.getFieldValue('revenueBreakdown')?.settlement || 0}
                onChange={e => handleRevenueBreakdownChange('settlement', parseFloat(e.target.value) || 0)}
                disabled={budgetLocked}
                min={0}
                style={{ paddingLeft: 2 }}
              />
            </div>
          </div>

          {/* 其他收入 */}
          <div className="revenue-item">
            <div className="revenue-item-label">其他收入</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <span style={{ fontFamily: 'var(--pf-font-mono)', fontSize: 14, fontWeight: 700, color: '#0f172a' }}>¥</span>
              <input
                type="number"
                className="revenue-native-input"
                value={form.getFieldValue('revenueBreakdown')?.other || 0}
                onChange={e => handleRevenueBreakdownChange('other', parseFloat(e.target.value) || 0)}
                disabled={budgetLocked}
                min={0}
                style={{ paddingLeft: 2 }}
              />
            </div>
          </div>

          {/* 累计收入汇总 */}
          <div className="revenue-item total-item" style={{ padding: '10px 12px' }}>
            <div className="revenue-summary">
              <div className="revenue-summary-label">
                <RiseOutlined style={{ fontSize: 13, opacity: 0.8 }} />
                累计收入
              </div>
              <div className="revenue-summary-value">
                ¥{revenueTotal.toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        {/* 预估利润卡片 */}
        <div className="project-form-profit-card">
          <div className="project-form-profit-header">
            <ExclamationCircleOutlined style={{ marginRight: 4 }} />
            预估利润分析
          </div>
          <div className="project-form-profit-metrics">
            <div>
              <div className="project-form-profit-metric-label">累计收入</div>
              <div className="project-form-profit-metric-value teal">
                ¥{revenueTotal.toLocaleString()}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div className="project-form-profit-metric-label">预估利润</div>
              <div className={`project-form-profit-metric-value ${profitEstimate.profit >= 0 ? 'green' : ''}`}
                style={{ color: profitEstimate.profit < 0 ? '#dc2626' : undefined }}>
                {profitEstimate.profit >= 0 ? '+' : ''}¥{profitEstimate.profit.toLocaleString()}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="project-form-profit-metric-label">利润率</div>
              <div className={`project-form-profit-metric-value ${profitEstimate.margin >= 0 ? 'green' : ''}`}
                style={{ color: profitEstimate.margin < 0 ? '#dc2626' : undefined }}>
                {profitEstimate.margin >= 0 ? '+' : ''}{profitEstimate.margin}%
              </div>
            </div>
          </div>
        </div>

        {/* 版本历史 */}
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500, marginBottom: 6 }}>
            版本历史
          </div>
          {project?.budgetVersions?.length ? renderVersionTimeline() : (
            <div style={{ fontSize: 12, color: '#94a3b8' }}>暂无版本记录</div>
          )}
        </div>
      </div>

    </div>
  );

  // ==================== MAIN RENDER ====================
  return (
    <ConfigProvider
      theme={{
        components: {
          Modal: { borderRadiusLG: 16 },
        },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoScrollToError
        initialValues={{
          status: ProjectStatus.ACTIVE,
          budget: 0,
          budgetBreakdown: DEFAULT_BUDGET_BREAKDOWN,
          revenueBreakdown: DEFAULT_REVENUE_BREAKDOWN,
          budgetVersionReason: '',
          progress: 0,
        }}
      >
        {/* 自定义头部 */}
        {renderCustomHeader()}

        {/* 移动端导航 */}
        {renderMobileNav()}

        {/* 主体内容区 */}
        <div className="pf-modal-body-wrap pf-fade-in">
          <div className="main-grid">
            {renderLeftColumn()}
            {renderRightColumn()}
          </div>
        </div>

        {/* 自定义底部 */}
        {renderCustomFooter()}
      </Form>
    </ConfigProvider>
  );
};

export default ProjectForm;
